import { useEffect, useState, useRef } from 'react';
import { resourceDocumentsAPI, productsAPI, productLinesAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Plus, Search, FileText, Trash2, Pencil, Upload, Download, Eye, X, 
  Book, Wrench, HelpCircle, Shield, Video, FileQuestion, AlertCircle,
  Bold, Italic, Underline, List
} from 'lucide-react';

const CATEGORY_OPTIONS = [
  { value: 'user_manual', label: 'User Manual', icon: Book, color: 'bg-blue-100 text-blue-700' },
  { value: 'installation_guide', label: 'Installation Guide', icon: Wrench, color: 'bg-amber-100 text-amber-700' },
  { value: 'troubleshooting', label: 'Troubleshooting', icon: HelpCircle, color: 'bg-red-100 text-red-700' },
  { value: 'warranty_terms', label: 'Warranty Terms', icon: Shield, color: 'bg-emerald-100 text-emerald-700' },
  { value: 'safety_info', label: 'Safety Information', icon: AlertCircle, color: 'bg-orange-100 text-orange-700' },
  { value: 'specifications', label: 'Specifications', icon: FileText, color: 'bg-cyan-100 text-cyan-700' },
  { value: 'faq', label: 'FAQ', icon: FileQuestion, color: 'bg-slate-100 text-slate-700' },
  { value: 'video_tutorial', label: 'Video Tutorial', icon: Video, color: 'bg-purple-100 text-purple-700' },
  { value: 'other', label: 'Other', icon: FileText, color: 'bg-slate-100 text-slate-700' },
];

export default function ResourceDocumentsPage() {
  const { hasRole } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [products, setProducts] = useState([]);
  const [productLines, setProductLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'other',
    content: '',
    file_url: '',
    file_type: '',
    file_size: 0,
    product_ids: [],
    product_line_ids: [],
    is_public: true,
    display_order: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [docsRes, productsRes, plRes] = await Promise.all([
        resourceDocumentsAPI.getAll(),
        productsAPI.getAll(),
        productLinesAPI.getAll()
      ]);
      setDocuments(docsRes.data || []);
      setProducts(productsRes.data || []);
      setProductLines(plRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleProductToggle = (productId) => {
    setFormData(prev => ({
      ...prev,
      product_ids: prev.product_ids.includes(productId)
        ? prev.product_ids.filter(id => id !== productId)
        : [...prev.product_ids, productId]
    }));
  };

  const handleProductLineToggle = (plId) => {
    setFormData(prev => ({
      ...prev,
      product_line_ids: prev.product_line_ids.includes(plId)
        ? prev.product_line_ids.filter(id => id !== plId)
        : [...prev.product_line_ids, plId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await resourceDocumentsAPI.create(formData);
      toast.success('Document created');
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create document');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    
    try {
      await resourceDocumentsAPI.update(selectedDocument.id, formData);
      toast.success('Document updated');
      setIsEditDialogOpen(false);
      setSelectedDocument(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update document');
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      await resourceDocumentsAPI.delete(docId);
      toast.success('Document deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'other',
      content: '',
      file_url: '',
      file_type: '',
      file_size: 0,
      product_ids: [],
      product_line_ids: [],
      is_public: true,
      display_order: 0
    });
  };

  const openEditDialog = (doc) => {
    setSelectedDocument(doc);
    setFormData({
      title: doc.title || '',
      description: doc.description || '',
      category: doc.category || 'other',
      content: doc.content || '',
      file_url: doc.file_url || '',
      file_type: doc.file_type || '',
      file_size: doc.file_size || 0,
      product_ids: doc.product_ids || [],
      product_line_ids: doc.product_line_ids || [],
      is_public: doc.is_public !== false,
      display_order: doc.display_order || 0
    });
    setIsEditDialogOpen(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 50 * 1024 * 1024) {
      toast.error('File size must be under 50MB');
      return;
    }

    setUploading(true);
    try {
      const response = await resourceDocumentsAPI.uploadFile(file);
      handleChange('file_url', response.data.file_url);
      handleChange('file_type', response.data.file_type);
      handleChange('file_size', response.data.file_size);
      toast.success('File uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const execCommand = (command) => {
    document.execCommand(command, false, null);
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          doc.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || doc.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getCategoryInfo = (category) => {
    return CATEGORY_OPTIONS.find(c => c.value === category) || CATEGORY_OPTIONS[CATEGORY_OPTIONS.length - 1];
  };

  // Render the document form fields
  const renderDocumentFormFields = () => (
    <>
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2 space-y-2">
          <Label htmlFor="title">Title *</Label>
          <Input
            id="title"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            required
            data-testid="doc-title-input"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="category">Category *</Label>
          <Select value={formData.category} onValueChange={(v) => handleChange('category', v)}>
            <SelectTrigger data-testid="doc-category-select">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {CATEGORY_OPTIONS.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="display_order">Display Order</Label>
          <Input
            id="display_order"
            type="number"
            value={formData.display_order}
            onChange={(e) => handleChange('display_order', parseInt(e.target.value) || 0)}
            data-testid="doc-order-input"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
          rows={2}
          data-testid="doc-description-input"
        />
      </div>

      {/* File Upload */}
      <div className="space-y-2">
        <Label>File Attachment</Label>
        {formData.file_url ? (
          <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-md">
            <FileText className="h-5 w-5 text-blue-600" />
            <span className="flex-1 text-sm truncate">{formData.file_url.split('/').pop()}</span>
            <span className="text-xs text-muted-foreground">{formatFileSize(formData.file_size)}</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${formData.file_url}`, '_blank')}
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                handleChange('file_url', '');
                handleChange('file_type', '');
                handleChange('file_size', 0);
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div 
            className="border-2 border-dashed rounded-md p-4 text-center cursor-pointer hover:bg-slate-50 transition-colors"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm font-medium">Click to upload a file</p>
            <p className="text-xs text-muted-foreground mt-1">PDF, Word, Videos, max 50MB</p>
            {uploading && <p className="text-xs text-blue-600 mt-2">Uploading...</p>}
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFileUpload}
        />
      </div>

      {/* Rich Text Content */}
      <div className="space-y-2">
        <Label>Content (Rich Text)</Label>
        <div className="flex flex-wrap gap-1 p-2 border rounded-t-md bg-slate-50">
          <Button type="button" variant="ghost" size="sm" onClick={() => execCommand('bold')} className="h-8 w-8 p-0">
            <Bold className="h-4 w-4" />
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => execCommand('italic')} className="h-8 w-8 p-0">
            <Italic className="h-4 w-4" />
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => execCommand('underline')} className="h-8 w-8 p-0">
            <Underline className="h-4 w-4" />
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => execCommand('insertUnorderedList')} className="h-8 w-8 p-0">
            <List className="h-4 w-4" />
          </Button>
        </div>
        <div
          contentEditable
          className="min-h-[100px] p-3 border border-t-0 rounded-b-md focus:outline-none focus:ring-2 focus:ring-blue-500 prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: formData.content }}
          onBlur={(e) => handleChange('content', e.target.innerHTML)}
          data-testid="doc-content-editor"
        />
      </div>

      {/* Product Association */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Link to Products</Label>
          <ScrollArea className="h-32 border rounded-md p-2">
            {products.map(product => (
              <div key={product.id} className="flex items-center space-x-2 py-1">
                <Checkbox
                  id={`product-${product.id}`}
                  checked={formData.product_ids.includes(product.id)}
                  onCheckedChange={() => handleProductToggle(product.id)}
                />
                <label htmlFor={`product-${product.id}`} className="text-sm cursor-pointer">
                  {product.name}
                </label>
              </div>
            ))}
            {products.length === 0 && <p className="text-xs text-muted-foreground">No products available</p>}
          </ScrollArea>
        </div>
        
        <div className="space-y-2">
          <Label>Link to Product Lines</Label>
          <ScrollArea className="h-32 border rounded-md p-2">
            {productLines.map(pl => (
              <div key={pl.id} className="flex items-center space-x-2 py-1">
                <Checkbox
                  id={`pl-${pl.id}`}
                  checked={formData.product_line_ids.includes(pl.id)}
                  onCheckedChange={() => handleProductLineToggle(pl.id)}
                />
                <label htmlFor={`pl-${pl.id}`} className="text-sm cursor-pointer flex items-center gap-1">
                  <Badge className={pl.color} variant="outline">{pl.name}</Badge>
                </label>
              </div>
            ))}
            {productLines.length === 0 && <p className="text-xs text-muted-foreground">No product lines available</p>}
          </ScrollArea>
        </div>
      </div>

      {/* Visibility */}
      <div className="flex items-center space-x-2">
        <Checkbox
          id="is_public"
          checked={formData.is_public}
          onCheckedChange={(checked) => handleChange('is_public', checked)}
        />
        <label htmlFor="is_public" className="text-sm cursor-pointer">
          Public (visible to all portal users)
        </label>
      </div>
    </>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="resource-documents-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] heading-tight">Resource Documents</h1>
          <p className="text-muted-foreground mt-1">Manage customer-facing documents and resources</p>
        </div>
        {hasRole(['admin']) && (
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button data-testid="add-document-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Document
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add Resource Document</DialogTitle>
                <DialogDescription>
                  Create a new document for the customer portal
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {renderDocumentFormFields()}
                <DialogFooter>
                  <Button type="submit" disabled={uploading} data-testid="doc-submit-btn">
                    Create Document
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search documents..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="doc-search-input"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORY_OPTIONS.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Document</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Products</TableHead>
                <TableHead>Visibility</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDocuments.length > 0 ? (
                filteredDocuments.map((doc) => {
                  const catInfo = getCategoryInfo(doc.category);
                  const IconComponent = catInfo.icon;
                  
                  return (
                    <TableRow key={doc.id} className="data-table-row" data-testid={`doc-row-${doc.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className={`h-9 w-9 rounded-sm flex items-center justify-center ${catInfo.color}`}>
                            <IconComponent className="h-4 w-4" />
                          </div>
                          <div>
                            <p className="font-medium">{doc.title}</p>
                            {doc.description && (
                              <p className="text-xs text-muted-foreground line-clamp-1">{doc.description}</p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={catInfo.color}>{catInfo.label}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {doc.product_ids?.slice(0, 2).map(pid => {
                            const product = products.find(p => p.id === pid);
                            return product ? (
                              <Badge key={pid} variant="outline" className="text-xs">{product.name}</Badge>
                            ) : null;
                          })}
                          {doc.product_ids?.length > 2 && (
                            <Badge variant="outline" className="text-xs">+{doc.product_ids.length - 2}</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={doc.is_public ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                          {doc.is_public ? 'Public' : 'Private'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {doc.file_url && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${doc.file_url}`, '_blank')}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(doc)}
                            data-testid={`edit-doc-${doc.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(doc.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    No documents found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={(open) => {
        setIsEditDialogOpen(open);
        if (!open) {
          setSelectedDocument(null);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Document</DialogTitle>
            <DialogDescription>
              Update document details and content
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            {renderDocumentFormFields()}
            <DialogFooter>
              <Button type="submit" disabled={uploading} data-testid="doc-update-btn">
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
